import React, { useEffect } from "react";
import { X } from "lucide-react"; // Assuming you use lucide-react or similar

type ModalVariant = "simple" | "banner";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  icon?: React.ReactNode;
  variant?: ModalVariant;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  bannerColorClass?: string; // e.g., 'bg-sky-500'
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  description,
  icon,
  variant = "simple",
  children,
  footer,
  bannerColorClass = "bg-[#3CA7D9]", // Default blue from screenshot 1
}) => {
  // Handle ESC key to close
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Modal Container */}
      <div className="relative z-10 w-full max-w-[500px] bg-white rounded-2xl shadow-2xl overflow-hidden transform transition-all animate-in fade-in zoom-in-95 duration-200">
        
        {/* --- VARIANT: BANNER HEADER (Screenshot 1) --- */}
        {variant === "banner" && (
          <div className={`${bannerColorClass} p-8 text-center flex flex-col items-center justify-center text-white relative`}>
            {/* Optional Close Icon Top Right */}
            {/* <button onClick={onClose} className="absolute top-4 right-4 text-white/80 hover:text-white">
               <X size={20} />
            </button> */}
            
            {icon && <div className="mb-4">{icon}</div>}
            <h2 className="text-2xl font-bold mb-2">{title}</h2>
            {description && <p className="text-white/90 text-sm font-light">{description}</p>}
          </div>
        )}

        {/* --- BODY CONTENT --- */}
        <div className="p-6">
          
          {/* --- VARIANT: SIMPLE HEADER (Screenshot 2) --- */}
          {variant === "simple" && (
            <div className="text-center mb-6">
              {icon && <div className="flex justify-center mb-4">{icon}</div>}
              <h2 className="text-xl font-bold text-slate-900 mb-2">{title}</h2>
              {description && <p className="text-slate-500 text-sm leading-relaxed max-w-[80%] mx-auto">{description}</p>}
            </div>
          )}

          {/* Custom Children */}
          <div className="text-slate-600 text-sm">
            {children}
          </div>
        </div>

        {/* --- FOOTER --- */}
        {footer && (
          <div className={`p-6 pt-0 ${variant === 'banner' ? 'mt-4' : ''}`}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

// --- Helper Button Component to match designs ---
interface ModalButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "outline" | "ghost";
  fullWidth?: boolean;
}

export const ModalButton: React.FC<ModalButtonProps> = ({ 
  className, 
  variant = "primary", 
  fullWidth = false, 
  children, 
  ...props 
}) => {
  const baseStyles = "px-4 py-3 rounded-lg font-medium transition-colors text-sm flex items-center justify-center gap-2";
  
  const variants = {
    primary: "bg-[#3CA7D9] hover:bg-[#3596c4] text-white shadow-sm", // Blue from screenshot
    outline: "border border-slate-200 text-slate-600 hover:bg-slate-50 bg-white",
    ghost: "bg-transparent text-slate-500 hover:text-slate-700",
  };

  return (
    <button 
      type={props.type ?? "button"}
      className={`${baseStyles} ${variants[variant]} ${fullWidth ? 'w-full' : ''} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
};
